import { useRouter } from "expo-router";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Image,
  Platform,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from "react-native";
import Icon from "react-native-vector-icons/FontAwesome";
import { useDispatch, useSelector } from "react-redux";

import { getorderPrint } from "../redux/actions/masteractions";
import { useLanguage } from "./LanguageProvider";

export default function OrderPrint() {
  const router = useRouter();
  const dispatch = useDispatch();
  const { t } = useLanguage();
  const { width } = useWindowDimensions();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const orderprint =
    useSelector((state: any) => state?.master?.orderprint) || [];

  // Web/Tablet แสดง 2 คอลัมน์ ส่วนมือถือแสดง 1 คอลัมน์
  const isWideScreen = width >= 760;
  const numberOfColumns = isWideScreen ? 2 : 1;

  // หน่วงการค้นหา ป้องกันการยิง API ทุกตัวอักษร
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // โหลดรายการ Order
  useEffect(() => {
    let isMounted = true;

    const loadOrderPrint = async () => {
      try {
        if (isMounted) {
          setIsLoading(true);
        }

        await dispatch(getorderPrint(debouncedSearch) as any);
      } catch (error) {
        console.error("getorderPrint error:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadOrderPrint();

    return () => {
      isMounted = false;
    };
  }, [debouncedSearch, dispatch]);

  const listData = useMemo(() => {
    return Array.isArray(orderprint) ? orderprint : [];
  }, [orderprint]);

  const openDetail = (item: any) => {
    router.push({
      pathname: "/screens/detailprint",
      params: {
        item: JSON.stringify(item),
      },
    });
  };

  const openPrint = (item: any) => {
    // เก็บข้อมูลสำรองสำหรับ Web ป้องกันข้อมูลหายเมื่อ Refresh
    if (Platform.OS === "web" && typeof window !== "undefined") {
      try {
        sessionStorage.setItem("currentPrintOrder", JSON.stringify(item));
      } catch (error) {
        console.warn("Cannot save print order:", error);
      }
    }

    router.push({
      pathname: "/screens/print_order",
      params: {
        item: JSON.stringify(item),
      },
    });
  };

  const renderOrderItem = ({ item }: { item: any; index: number }) => {
    return (
      <View
        style={{
          width: isWideScreen ? "49%" : "100%",
          marginBottom: 14,
        }}
        className="rounded-xl bg-gray-100 border border-gray-200 overflow-hidden"
      >
        <View className="p-4">
          {/* รูปเครื่องพิมพ์ */}
          <View className="items-center justify-center mb-3">
            <Image
              source={require("../../assets/print.png")}
              resizeMode="contain"
              style={{
                width: 80,
                height: 80,
              }}
            />
          </View>

          {/* Material Description */}
          <Text
            numberOfLines={2}
            className="text-base text-center font-kanit font-semibold text-[#333] mb-3"
          >
            {item?.materialDesc || t("no_data")}
          </Text>

          {/* Order Number และพนักงาน */}
          <View className="flex-row w-full border border-gray-300 rounded-lg p-2 mb-2 bg-white">
            <View className="flex-1 flex-row items-center pr-2">
              <Icon name="file" size={14} color="#F17736" />

              <Text
                numberOfLines={1}
                className="flex-1 text-sm font-kanit text-gray-500 ml-2"
              >
                {item?.order_number || t("no_data")}
              </Text>
            </View>

            <View className="w-px bg-gray-200 mx-1" />

            <View className="flex-1 flex-row items-center pl-2">
              <Icon name="user" size={14} color="#F17736" />

              <Text
                numberOfLines={1}
                className="flex-1 text-sm font-kanit text-gray-500 ml-2"
              >
                {item?.employee_product || t("no_data")}
              </Text>
            </View>
          </View>

          {/* วันที่และเวลา */}
          <View className="flex-row w-full border border-gray-300 rounded-lg p-2 bg-white">
            <View className="flex-1 flex-row items-center pr-2">
              <Icon name="calendar" size={14} color="#F17736" />

              <Text
                numberOfLines={1}
                className="flex-1 text-sm font-kanit text-gray-500 ml-2"
              >
                {item?.createdAt
                  ? moment(item.createdAt).format("DD/MM/YYYY")
                  : t("no_data")}
              </Text>
            </View>

            <View className="w-px bg-gray-200 mx-1" />

            <View className="flex-1 flex-row items-center pl-2">
              <Icon name="clock-o" size={14} color="#F17736" />

              <Text
                numberOfLines={1}
                className="flex-1 text-sm font-kanit text-gray-500 ml-2"
              >
                {item?.createdAt
                  ? `${moment(item.createdAt).format("HH:mm")} น.`
                  : t("no_data")}
              </Text>
            </View>
          </View>

          {/* Buttons */}
          <View className="flex-row items-center justify-center mt-4">
            <TouchableOpacity
              activeOpacity={0.8}
              delayPressIn={80}
              onPress={() => openDetail(item)}
              className="flex-1 rounded-full bg-[#58d68d] py-2.5 items-center justify-center mr-1.5"
            >
              <Text className="text-white text-sm font-kanit">
                {t("detail")}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              activeOpacity={0.8}
              delayPressIn={80}
              onPress={() => openPrint(item)}
              className="flex-1 rounded-full bg-[#32ADE6] py-2.5 items-center justify-center ml-1.5"
            >
              <Text className="text-white text-sm font-kanit">
                {t("print")}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  };

  const renderEmptyComponent = () => {
    if (isLoading) {
      return null;
    }

    return (
      <View className="flex-1 items-center justify-center px-4 min-h-[380px]">
        <Image
          source={
            searchQuery.trim()
              ? require("../../assets/nosearch.png")
              : require("../../assets/search.png")
          }
          resizeMode="contain"
          style={{ width: 140, height: 140 }}
        />

        <Text className="text-xl text-center font-kanit text-gray-500 mt-4">
          {searchQuery.trim()
            ? t("no_search_data")
            : t("search_print_tag_list")}
        </Text>

        <Text className="text-base text-center font-kanit text-gray-400 mt-1">
          {searchQuery.trim()
            ? t("no_order_number")
            : t("please_enter_order_number")}
        </Text>
      </View>
    );
  };

  return (
    // 1. เพิ่ม height: '100%' ป้องกันจอยืดทะลุในบาง Container ของ Expo
    <View style={{ flex: 1, height: "100%" }} className="bg-[#F17736]">
      {/* Background เท่านั้น ไม่ให้ดัก Mouse/Touch */}
      <View
        pointerEvents="none"
        className="absolute top-0 left-0 right-0 h-[20%] bg-[#F17736]"
      />
      <View
        pointerEvents="none"
        className="absolute top-[18%] left-0 right-0 bottom-0 bg-white rounded-t-[20px]"
      />

      {/* 2. Main Card เปลี่ยนจาก position: 'absolute' มาเป็นการใช้ flex: 1 แทน เพื่อรักษาสมดุลของ Flexbox Chain */}
      <View
        style={{
          flex: 1,
          marginTop: 20,
          marginBottom: 95, // เว้นที่ให้ Menubar ด้านล่าง
          maxWidth: 1100,
          alignSelf: "center",
          width: width >= 1200 ? "90%" : "94%",
          backgroundColor: "white",
          borderRadius: 16,
          borderColor: "#E5E7EB",
          borderWidth: 1,
          overflow: "hidden", // ป้องกันหัวมุมล้น
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.1,
          shadowRadius: 4,
          elevation: 3,
        }}
      >
        {/* Header และช่องค้นหาไม่เลื่อนตามรายการ */}
        <View className="px-5 pt-5 bg-white z-10">
          <View className="w-full items-center justify-center border-b border-gray-300 pb-3">
            <Text className="text-2xl font-kanit">{t("print_tag_list")}</Text>
          </View>

          <View className="relative w-full flex-row items-center py-4">
            <TextInput
              placeholder={t("search_order_number")}
              placeholderTextColor="#9CA3AF"
              value={searchQuery}
              onChangeText={setSearchQuery}
              autoCapitalize="none"
              autoCorrect={false}
              returnKeyType="search"
              className="w-full h-12 border border-gray-300 rounded-lg px-4 pr-12 bg-gray-50 font-kanit"
            />

            {searchQuery.length > 0 && (
              <TouchableOpacity
                activeOpacity={0.7}
                onPress={() => setSearchQuery("")}
                className="absolute right-3 h-12 w-10 items-center justify-center"
              >
                <Icon name="times-circle" size={24} color="red" />
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* 3. เอา overflow: 'hidden' ในคอนเทนเนอร์นี้ออก และใช้แค่ flex: 1 */}
        <View style={{ flex: 1 }} className="px-5">
          {isLoading ? (
            <View className="flex-1 items-center justify-center">
              <ActivityIndicator size="large" color="#F17736" />
              <Text className="text-black text-lg font-kanit mt-4">
                {t("loading_data")}
              </Text>
            </View>
          ) : (
            <FlatList
              key={`order-list-${numberOfColumns}`}
              style={{ flex: 1 }}
              data={listData}
              renderItem={renderOrderItem}
              keyExtractor={(item: any, index: number) =>
                String(item?.id ?? item?.order_number ?? item?.materialDoc ?? index)
              }
              numColumns={numberOfColumns}
              columnWrapperStyle={
                numberOfColumns === 2 ? { justifyContent: "space-between" } : undefined
              }
              contentContainerStyle={{
                flexGrow: 1, // สำคัญมาก เพื่อดันให้ Empty State อยู่ตรงกลาง และไม่บังคับความสูงรายการมากเกินไป
                paddingTop: 2,
                paddingBottom: 40,
              }}
              showsVerticalScrollIndicator={true}
              keyboardShouldPersistTaps="handled"
              keyboardDismissMode="on-drag"
              ListEmptyComponent={renderEmptyComponent}
            />
          )}
        </View>
      </View>
    </View>
  );
}